// Shared default disclaimer copy + a tiny plain-text renderer.
// The editable text lives in Mongo (disclaimer_config). When it is
// empty, both the admin editor and the agent-facing gate fall back to
// these defaults. Body text supports **bold** and "- " bullet lines.

export const DEFAULT_DISCLAIMER = {
  en: {
    title: 'Confidentiality & Use Acknowledgment',
    body: `All content, materials, training videos, marketing collateral, logos, branding, forms, sample files, pricing information, and any other resources available on this platform are **confidential and proprietary** to KR Enterprise Group and are provided for the **exclusive use of contracted agents and agencies** in connection with their authorized work with KR.

You agree that you will NOT:
- Share, distribute, resell, or republish any material outside the platform.
- Alter, modify, edit, deface, remove attribution from, or create derivative works of any logo, artwork, video, document, or template — including changing colors, copy, layout, or branding.
- Use any material for any purpose other than legitimate KR-affiliated agent activity (recruiting, training, member education, marketing of KR-approved products).
- Reverse-engineer, screen-scrape, or export bulk data from the platform.

All materials are protected by **federal copyright law** and KR retains all rights, title, and interest in the intellectual property. Unauthorized use, alteration, or misappropriation of any content may result in immediate termination of your access and may expose you and your agency to **civil and criminal liability under the laws of the State of Florida** and applicable federal statutes, including but not limited to actions for copyright infringement (17 U.S.C. § 501), misappropriation of trade secrets (Fla. Stat. § 688), and civil theft (Fla. Stat. § 772.11).`,
  },
  es: {
    title: 'Reconocimiento de Confidencialidad y Uso',
    body: `Todo el contenido, los materiales, los videos de entrenamiento, el material de mercadeo, los logotipos, la identidad de marca, los formularios, los archivos de muestra, la información de precios y cualquier otro recurso disponible en esta plataforma son **confidenciales y propiedad** de KR Enterprise Group y se proveen para el **uso exclusivo de los agentes y agencias contratados** en el marco de su trabajo autorizado con KR.

Aceptas que NO harás lo siguiente:
- Compartir, distribuir, revender ni publicar ningún material fuera de la plataforma.
- Alterar, modificar, editar, dañar, quitar la autoría, ni crear obras derivadas de ningún logotipo, arte, video, documento o plantilla — incluyendo cambios de color, texto, diseño o identidad de marca.
- Usar cualquier material para fines distintos a la actividad legítima de agente afiliado a KR (reclutamiento, entrenamiento, educación de miembros, mercadeo de productos aprobados por KR).
- Hacer ingeniería inversa, extracción masiva de datos ni copiar contenido de la plataforma.

Todos los materiales están protegidos por la **ley federal de derechos de autor** y KR conserva todos los derechos, títulos e intereses sobre la propiedad intelectual. El uso no autorizado, la alteración o apropiación indebida de cualquier contenido puede resultar en la **terminación inmediata de tu acceso** y puede exponerte a ti y a tu agencia a **responsabilidad civil y criminal bajo las leyes del Estado de Florida** y estatutos federales aplicables, incluyendo pero no limitado a acciones por infracción de derechos de autor (17 U.S.C. § 501), apropiación indebida de secretos comerciales (Fla. Stat. § 688) y hurto civil (Fla. Stat. § 772.11).`,
  },
};

// Parse **bold** segments within a single line into React nodes.
function parseInline(text, keyPrefix) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) =>
    i % 2 === 1
      ? <strong key={`${keyPrefix}-b-${i}`}>{seg}</strong>
      : <span key={`${keyPrefix}-t-${i}`}>{seg}</span>
  );
}

// Render editable body text (blank-line-separated blocks; "- " bullets).
export function renderDisclaimerBody(body) {
  const blocks = String(body || '').replace(/\r\n/g, '\n').split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim().length > 0);
    if (lines.length === 0) return null;
    const isList = lines.every((l) => l.trim().startsWith('- '));
    if (isList) {
      return (
        <ul key={`blk-${bi}`} className="list-disc list-inside space-y-1 pl-2">
          {lines.map((l, li) => (
            <li key={`blk-${bi}-li-${li}`}>{parseInline(l.trim().slice(2), `blk-${bi}-li-${li}`)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`blk-${bi}`}>
        {lines.map((l, li) => (
          <span key={`blk-${bi}-ln-${li}`}>
            {parseInline(l, `blk-${bi}-ln-${li}`)}
            {li < lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}
